import type { StatusModel } from "../api/vegApl"

type StatusWithKey = StatusModel & { status_key?: string }

const normalize = (value?: string | null) => (value ?? "").trim().toLowerCase()

const normalizeUnderscore = (value?: string | null) => normalize(value).replace(/\s+/g, "_")

const collectStatusIdsByNames = (statuses: StatusWithKey[], names: string[]): number[] => {
    const nameSet = new Set(names.map(normalize))
    const ids = statuses
        .filter((item) => nameSet.has(normalize(item?.status_value)))
        .map((item) => item?.status_id)
        .filter((id): id is number => typeof id === "number")

    return Array.from(new Set(ids))
}

const collectStatusIdsByNamesOrKeys = (statuses: StatusWithKey[], values: string[], keys: string[] = []): number[] => {
    const valueSet = new Set(values.map(normalize))
    const keySet = new Set(keys.map(normalizeUnderscore))

    const ids = statuses
        .filter((item) => {
            const valueMatch = valueSet.has(normalize(item?.status_value))
            const keyMatch = keySet.has(normalizeUnderscore(item?.status_key))
            return valueMatch || keyMatch
        })
        .map((item) => item?.status_id)
        .filter((id): id is number => typeof id === "number")

    return Array.from(new Set(ids))
}

export const filterStatusesByKey = (statuses: StatusModel[], statusKey: string): StatusModel[] => {
    const desiredKey = normalize(statusKey)
    return (statuses as StatusWithKey[]).filter((item) => normalize(item?.status_key) === desiredKey)
}

export const getTrialStatusIdsByTab = (tab: string, trialStatuses: StatusModel[]): number[] => {
    const statuses = trialStatuses as StatusWithKey[]

    if (tab === "In Progress") {
        return collectStatusIdsByNamesOrKeys(statuses, ["In Progress"], ["in_progress"])
    }

    if (tab === "Ready for Harvest") {
        return collectStatusIdsByNamesOrKeys(statuses, ["Ready for Harvest", "Ready For Harvest"], ["ready_for_harvest"])
    }

    if (tab === "Completed") {
        return collectStatusIdsByNamesOrKeys(
            statuses,
            ["Completed", "Manually Completed"],
            ["completed", "manual_completed", "manually_completed"]
        )
    }

    return []
}

export const getSplotStatusIdsByTab = (tab: string, splotStatuses: StatusModel[]): number[] => {
    const statuses = splotStatuses as StatusWithKey[]
    const normalizedTab = normalize(tab)
    const allIds = statuses
        .map((item) => item?.status_id)
        .filter((id): id is number => typeof id === "number")

    const completedIds = collectStatusIdsByNames(statuses, ["Completed"])
    const createdIds = collectStatusIdsByNames(statuses, ["Created"])

    if (normalizedTab === "completed") {
        return completedIds
    }

    if (normalizedTab === "created") {
        return createdIds
    }

    if (normalizedTab === "processing") {
        const exclude = new Set([...completedIds, ...createdIds])
        return Array.from(new Set(allIds.filter((id) => !exclude.has(id))))
    }

    return []
}

type ResolveAccessibleFilterParams = {
    statusIds: unknown
    filters: string[]
    getIdsForFilter: (filter: string) => Array<string | number>
    excludedFilters?: string[]
}

export const resolveAccessibleFilterByStatusIds = ({
    statusIds,
    filters,
    getIdsForFilter,
    excludedFilters = [],
}: ResolveAccessibleFilterParams): string => {
    if (!Array.isArray(statusIds) || statusIds.length === 0) return ""

    const normalizeIds = (ids: Array<string | number>) => ids.map((id) => String(id)).sort()
    const selected = normalizeIds(statusIds as Array<string | number>)
    const tabFilters = filters.filter((filter) => !excludedFilters.includes(filter))

    for (const filter of tabFilters) {
        const idsForFilter = normalizeIds(getIdsForFilter(filter))
        if (idsForFilter.length === selected.length && idsForFilter.every((id, index) => id === selected[index])) {
            return filter
        }
    }

    return ""
}
